import { t as createLogger } from "./app.shared-CmP4XLF_.js";

import { t as debounce } from "./app.shared-OQDvbu1B.js";

var slider_component_default = (() => {
    if (customElements.get("slider-component")) return;
    customElements.define("slider-component", class SliderComponent extends HTMLElement {
        constructor() {
            super();
            this._rawLogger = createLogger("slider-component");
            this.isDebugEnabled = false;
            this.isSetup = false;
            this.isVisible = null;
            this.slideState = null;
            this.visibleSlideIndexes = new Set;
            this.markerObserver = null;
            this.displayObserver = null;
            this.controlsResizeObserver = null;
            this.controlsVisible = false;
            this.handleNext = this.handleNext.bind(this);
            this.handlePrevious = this.handlePrevious.bind(this);
            this._handleGoto = this._handleGoto.bind(this);
            this.checkControlsVisibility = debounce(this.checkControlsVisibility.bind(this), 100);
        }
        log(topic, message, detail) {
            if (this.isDebugEnabled) this._rawLogger(topic, message, detail);
        }
        connectedCallback() {
            this.isDebugEnabled = this.hasAttribute("data-debug-enabled");
            this.slider = this.querySelector('[id^="Slider-"]');
            if (!this.slider) return;
            this.slides = Array.from(this.querySelectorAll("[data-slide]"));
            this.slideCounter = this.querySelector("[data-current-slide]");
            this.slideTotal = this.querySelector("[data-slide-total]");
            this.isWindowObserver = this.hasAttribute("data-window-observer");
            this.counterOffset = Number.parseInt(this.dataset.counterOffset || "0", 10);
            if (Number.isNaN(this.counterOffset)) this.counterOffset = 0;
            this.log("lifecycle", "connected", {
                sliderId: this.dataset.sliderId || null,
                hiddenDestroy: this.hasAttribute("data-hidden-destroy")
            });
            if (this.hasAttribute("data-hidden-destroy")) {
                const isInitiallyVisible = this.offsetWidth > 0 || this.offsetHeight > 0;
                this.isVisible = isInitiallyVisible;
                if (isInitiallyVisible) this.init();
                if (!this.displayObserver) this.setupDisplayObserver();
            } else this.init();
        }
        init() {
            this.log("lifecycle", "init", {
                sliderId: this.dataset.sliderId || null,
                slides: this.slides.length
            });
            if (this.isSetup) return;
            this.isSetup = true;
            this.setupScrollObserver();
            this.setupControlsResizeObserver();
        }
        setupScrollObserver() {
            if (this.markerObserver) return;
            this.markerObserver = this.createScrollObserver();
            for (const slide of this.slides) this.markerObserver.observe(slide);
            this.addEventListener("slider:goto", this._handleGoto);
            this.addEventListener("slider:get-state", e => {
                this.refreshSlides();
                if (this.slider && this.slider.clientWidth > 0) this.controlsVisible = this.slider.scrollWidth > this.slider.clientWidth;
                if (typeof e.detail?.callback === "function") e.detail.callback({
                    ...this.slideState,
                    controlsVisible: this.controlsVisible,
                    canGoPrev: this._canGoPrev(),
                    canGoNext: this._canGoNext()
                });
            });
            this.addEventListener("slider:next", this.handleNext);
            this.addEventListener("slider:previous", this.handlePrevious);
            this.dispatchSliderInit();
        }
        _handleGoto(e) {
            const {index: index} = e.detail;
            if (typeof index !== "number") return;
            const slide = this.slides[index];
            if (slide) this.slider.scrollTo({
                left: slide.offsetLeft,
                behavior: "smooth"
            });
        }
        dispatchSliderInit() {
            const detail = {
                activeIndex: 0,
                total: this.slides.length + this.counterOffset,
                controlsVisible: this.controlsVisible,
                canGoPrev: this._canGoPrev(),
                canGoNext: this._canGoNext()
            };
            this.slideState = detail;
            if (this.slideTotal) this.slideTotal.textContent = detail.total;
            this.dispatchEvent(new CustomEvent("slider:init", {
                detail: detail,
                bubbles: true
            }));
            this.emitControlsState();
        }
        dispatchSliderChange(activeIndex) {
            const detail = {
                activeIndex: activeIndex,
                total: this.slides.length + this.counterOffset,
                controlsVisible: this.controlsVisible,
                canGoPrev: this._canGoPrev(),
                canGoNext: this._canGoNext()
            };
            this.slideState = detail;
            this.dispatchEvent(new CustomEvent("slider:change", {
                detail: detail,
                bubbles: true
            }));
            this.emitControlsState();
        }
        refreshSlides() {
            const currentSlides = Array.from(this.querySelectorAll("[data-slide]"));
            if (currentSlides.length === this.slides.length) return;
            this.slides = currentSlides;
            if (this.markerObserver) {
                this.markerObserver.disconnect();
                this.visibleSlideIndexes.clear();
                for (const slide of this.slides) this.markerObserver.observe(slide);
            }
        }
        checkControlsVisibility() {
            if (!this.slider || this.slider.clientWidth < 1) return;
            this.refreshSlides();
            const needsScrolling = this.slider.scrollWidth > this.slider.clientWidth;
            this.controlsVisible = needsScrolling;
            this.emitControlsState();
        }
        setupControlsResizeObserver() {
            if (this.controlsResizeObserver) return;
            this.controlsResizeObserver = new ResizeObserver(() => this.checkControlsVisibility());
            this.controlsResizeObserver.observe(this.slider);
            this.controlsResizeObserver.observe(this);
            this.checkControlsVisibility();
        }
        handleNext() {
            if (!this.slider) return;
            const movement = this.slides[0]?.clientWidth * Number(getComputedStyle(this).getPropertyValue("--movement_columns") || 1);
            if (!isNaN(movement)) this.slider.scrollTo({
                left: this.slider.scrollLeft + movement,
                behavior: "smooth"
            });
        }
        handlePrevious() {
            if (!this.slider) return;
            const movement = this.slides[0]?.clientWidth * Number(getComputedStyle(this).getPropertyValue("--movement_columns") || 1);
            if (!isNaN(movement)) this.slider.scrollTo({
                left: this.slider.scrollLeft - movement,
                behavior: "smooth"
            });
        }
        _canGoPrev() {
            if (!this.slides.length || !this.controlsVisible) return false;
            if (this.visibleSlideIndexes.size) return !this.visibleSlideIndexes.has(0);
            return this.slider.scrollLeft > 1;
        }
        _canGoNext() {
            if (!this.slides.length || !this.controlsVisible) return false;
            if (this.visibleSlideIndexes.size) return !this.visibleSlideIndexes.has(this.slides.length - 1);
            return this.slider.scrollLeft + this.slider.clientWidth < this.slider.scrollWidth - 1;
        }
        emitControlsState() {
            this.dispatchEvent(new CustomEvent("slider:controls-state", {
                detail: {
                    activeIndex: this.slideState?.activeIndex ?? 0,
                    total: this.slideState?.total ?? this.slides.length + this.counterOffset,
                    controlsVisible: this.controlsVisible,
                    canGoPrev: this._canGoPrev(),
                    canGoNext: this._canGoNext()
                },
                bubbles: true
            }));
        }
        updateSlideCounter(index) {
            if (this.slideCounter) this.slideCounter.textContent = index + 1 + this.counterOffset;
        }
        createScrollObserver() {
            return new IntersectionObserver(entries => {
                for (const entry of entries) {
                    const index = this.slides.indexOf(entry.target);
                    if (index === -1) continue;
                    entry.isIntersecting ? this.visibleSlideIndexes.add(index) : this.visibleSlideIndexes.delete(index);
                }
                if (this.visibleSlideIndexes.size === 0) return;
                const lastIdx = this.slides.length - 1;
                const displayIdx = this.visibleSlideIndexes.has(lastIdx) ? lastIdx : Math.min(...this.visibleSlideIndexes);
                this.updateSlideCounter(displayIdx);
                this.dispatchSliderChange(displayIdx);
            }, {
                root: this.isWindowObserver ? null : this.slider,
                threshold: .95
            });
        }
        setupDisplayObserver() {
            this.displayObserver = new ResizeObserver(entries => {
                const isNowVisible = entries[0].contentRect.width > 0;
                if (this.isVisible !== isNowVisible) {
                    isNowVisible ? this.init() : this.destroy();
                    this.isVisible = isNowVisible;
                }
            });
            this.displayObserver.observe(this);
        }
        destroy() {
            this.isSetup = false;
            this.removeEventListener("slider:goto", this._handleGoto);
            this.removeEventListener("slider:next", this.handleNext);
            this.removeEventListener("slider:previous", this.handlePrevious);
            if (this.markerObserver) {
                this.markerObserver.disconnect();
                this.markerObserver = null;
            }
            if (this.controlsResizeObserver) {
                this.controlsResizeObserver.disconnect();
                this.controlsResizeObserver = null;
            }
            this.controlsVisible = false;
            this.emitControlsState();
        }
        disconnectedCallback() {
            this.destroy();
            this.displayObserver?.disconnect();
        }
    });
})();